import { useState, useEffect, useMemo, useCallback } from "react";
import Table from "components/Common/Table/Table";
import DummyWeightInputTabContainer from "./DummyWeightInputTab.styles";
import * as Database from "utils/database.utils";
import { useSettings } from "context/Settings.context";
import { CompanyData, WeightMode, WeightsResponse, AnalystInfo } from "./types";
import {
    formatWeight,
    shortenName,
    getAnalystWeight,
    getAnalystRationale,
    formatWeightForEdit,
    buildProcessedData,
    buildTotalsRow,
} from "../utils";
import { BASE_COLUMNS } from "../columns";
import WeightCell from "../WeightCell";

const DummyWeightInputTab = () => {
    const { setToast } = useSettings();
    const [rawData, setRawData] = useState<CompanyData[]>([]);
    const [allAnalysts, setAllAnalysts] = useState<AnalystInfo[]>([]);
    const [loading, setLoading] = useState(true);
    const [totals, setTotals] = useState<WeightsResponse["totals"] | null>(
        null
    );
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editValue, setEditValue] = useState<string>("");
    const [editMode, setEditMode] = useState<WeightMode>("custom");
    const [editRationale, setEditRationale] = useState<string>("");
    const [saving, setSaving] = useState(false);

    const currentUserId = localStorage.getItem("gim-user-id");

    const fetchData = useCallback(async () => {
        setLoading(true);
        const response = await Database.get(
            "api/team-dummy-portfolios/analyst-team-weights"
        );
        if (response.ok) {
            const result: WeightsResponse = await response.json();
            setRawData(result.companies);
            setTotals(result.totals);

            // Extract analysts from totals
            const analysts: AnalystInfo[] = result.totals.analyst_totals.map(
                (a) => ({
                    analyst_id: a.analyst_id,
                    analyst_name: a.analyst_name,
                })
            );

            setAllAnalysts(analysts);
        } else {
            setRawData([]);
            setAllAnalysts([]);
            setTotals(null);
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const processedData = useMemo(
        () => buildProcessedData(rawData, allAnalysts),
        [rawData, allAnalysts]
    );

    const totalsObj = useMemo(
        () => (totals ? buildTotalsRow(totals, allAnalysts) : null),
        [totals, allAnalysts]
    );

    const handleEdit = useCallback(
        (record: CompanyData) => {
            setEditingId(record.company_id);
            const currentWeight = currentUserId
                ? getAnalystWeight(record, currentUserId)
                : undefined;
            setEditValue(
                currentWeight !== null && currentWeight !== undefined
                    ? formatWeightForEdit(currentWeight)
                    : ""
            );
            setEditMode("custom");
            setEditRationale(
                currentUserId ? getAnalystRationale(record, currentUserId) ?? "" : ""
            );
        },
        [currentUserId]
    );

    const handleModeChange = useCallback(
        (mode: WeightMode) => {
            setEditMode(mode);
            if (mode === "live" && editingId) {
                const company = rawData.find((c) => c.company_id === editingId);
                if (company) {
                    setEditValue(
                        company.portfolio_weight !== null
                            ? formatWeightForEdit(company.portfolio_weight)
                            : ""
                    );
                }
            }
        },
        [editingId, rawData]
    );

    const handleSave = useCallback(
        async (companyId: string) => {
            if (saving) return;
            setSaving(true);

            let parsedValue = null;
            if (editMode === "custom" && editValue !== "") {
                parsedValue = parseFloat(editValue) / 100;
            }

            try {
                const response = await Database.post(
                    "api/team-dummy-portfolios/dummy-weights",
                    {
                        weights: [
                            {
                                company_id: companyId,
                                dummy_weight: parsedValue,
                                rationale: editRationale.trim() || null,
                            },
                        ],
                    }
                );

                if (response.ok) {
                    setToast({
                        show: true,
                        type: "success",
                        message: "Weight saved successfully",
                    });
                    await fetchData();
                } else {
                    setToast({
                        show: true,
                        type: "error",
                        message: "Failed to save weight",
                    });
                }
            } finally {
                setSaving(false);
                setEditingId(null);
                setEditValue("");
                setEditMode("custom");
                setEditRationale("");
            }
        },
        [saving, editMode, editValue, editRationale, fetchData, setToast]
    );

    const handleCancel = useCallback(() => {
        setEditingId(null);
        setEditValue("");
        setEditMode("custom");
        setEditRationale("");
    }, []);

    const formatDate = (isoDate: string | null): string => {
        if (!isoDate) return "Null";
        const date = new Date(isoDate);
        return date.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
        });
    };


    // Separate current user from other analysts (current user first)
    const filteredAnalysts = useMemo(
        () => allAnalysts.filter((analyst) => analyst.analyst_name),
        [allAnalysts]
    );

    const sortedAnalysts = useMemo(() => {
        const currentUserAnalyst = filteredAnalysts.find(
            (analyst) => analyst.analyst_id === currentUserId
        );
        const otherAnalysts = filteredAnalysts.filter(
            (analyst) => analyst.analyst_id !== currentUserId
        );
        return currentUserAnalyst
            ? [currentUserAnalyst, ...otherAnalysts]
            : otherAnalysts;
    }, [filteredAnalysts, currentUserId]);

    const analystColumns = useMemo(
        () =>
            sortedAnalysts.map((analyst, index) => ({
                id: 100 + index,
                title: shortenName(analyst.analyst_name).toUpperCase(),
                sortable: true,
                value: `analyst_${analyst.analyst_id}`,
                cell: (rowOrValue: CompanyData | number) => {
                    if (typeof rowOrValue === "number") {
                        return formatWeight(rowOrValue);
                    }
                    const isCurrentUser = currentUserId === analyst.analyst_id;
                    const isEditing =
                        editingId === rowOrValue.company_id && isCurrentUser;

                    return (
                        <WeightCell
                            weight={getAnalystWeight(
                                rowOrValue,
                                analyst.analyst_id
                            )}
                            portfolioWeight={rowOrValue.portfolio_weight}
                            rationale={getAnalystRationale(
                                rowOrValue,
                                analyst.analyst_id
                            )}
                            editable={isCurrentUser}
                            isEditing={isEditing}
                            isSaving={isEditing && saving}
                            editValue={editValue}
                            editMode={editMode}
                            editRationale={editRationale}
                            companyId={rowOrValue.company_id}
                            companyName={rowOrValue.company_name}
                            bbgTicker={rowOrValue.bbg_ticker || undefined}
                            setEditValue={setEditValue}
                            setEditRationale={setEditRationale}
                            onModeChange={handleModeChange}
                            onEdit={() => handleEdit(rowOrValue)}
                            onSave={handleSave}
                            onCancel={handleCancel}
                        />
                    );
                },
                show: true,
                fullDataRow: true,
            })),
        [
            sortedAnalysts,
            currentUserId,
            editingId,
            saving,
            editValue,
            editMode,
            editRationale,
            handleEdit,
            handleModeChange,
            handleSave,
            handleCancel,
        ]
    );

    const lastColumns = useMemo(
        () => [
            {
                id: 9,
                title: "LAST UPDATED",
                sortable: true,
                value: "updated_at",
                cell: (row: CompanyData) => formatDate(row.updated_at),
                show: true,
                fullDataRow: true,
                showTotals: false,
            },
        ],
        []
    );

    const columns = useMemo(
        () => [...BASE_COLUMNS, ...analystColumns, ...lastColumns],
        [analystColumns, lastColumns]
    );

    return (
        <DummyWeightInputTabContainer>
            <Table
                data={processedData}
                columns={columns}
                filterQuery=""
                emptyText="No securities found for your coverage"
                defaultSortKey="portfolio_weight"
                defaultSortOrder="DESC"
                total={rawData.length}
                loading={loading}
                showTotals={rawData.length > 0}
                totals={totalsObj}
                totalsLabel="TOTAL"
            />
        </DummyWeightInputTabContainer>
    );
};

export default DummyWeightInputTab;
